<!--
 * @Author: Christion.Young
 * @Date: 2025-07-30 10:00:00
 * @LastEditors: Christion.Young
 * @LastEditTime: 2025-07-30 10:00:00
 * @Description: 商家列表页面 - 红色主题，无移动端适配
 * @Features:
 *   - 商家列表展示
 *   - 分页显示
 *   - PC端专用设计
-->
<template>
  <div class="merchantlist-page">
    <div class="merchantlist-container">
      <div class="merchantlist-header">
        <h1>推荐列表</h1>
        <p>发现优质商家，享受优质服务</p>
      </div>

      <!-- 搜索筛选区域 -->
      <div class="merchantlist-filter">
        <div class="filter-section">
          <div class="search-box">
            <label class="filter-label">搜索商家：</label>
            <div class="search-input-wrapper">
              <input
                v-model="searchKeyword"
                type="text"
                class="search-input"
                placeholder="请输入商家名称"
                @input="handleSearch"
                @keyup.enter="handleSearch"
              />
              <button
                v-if="searchKeyword"
                class="clear-btn"
                @click="clearSearch"
                title="清除搜索"
              >
                <i class="el-icon-close"></i>
              </button>
            </div>
          </div>
          <div v-if="searchKeyword" class="search-status">
            <span
              >搜索"{{ searchKeyword }}"，找到
              {{ filteredMerchantList.length }} 个商家</span
            >
          </div>
        </div>
      </div>

      <div class="merchantlist-content">
        <!-- 商家列表 -->
        <div class="merchantlist-list">
          <!-- 加载状态 -->
          <div v-if="loading" class="merchantlist-loading-wrapper">
            <div class="merchantlist-skeleton-container">
              <div class="merchantlist-skeleton-card" v-for="n in 9" :key="n">
                <div class="merchantlist-skeleton-image"></div>
                <div class="skeleton-content">
                  <div class="merchantlist-skeleton-line name"></div>
                  <div class="merchantlist-skeleton-line desc"></div>
                  <div class="merchantlist-skeleton-line desc"></div>
                  <div class="merchantlist-skeleton-line location"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- 空状态 -->
          <div
            v-else-if="filteredMerchantList.length === 0"
            class="merchantlist-empty-state"
          >
            <i class="el-icon-shop"></i>
            <p v-if="searchKeyword">未找到包含"{{ searchKeyword }}"的商家</p>
            <p v-else>暂无相关商家</p>
          </div>

          <!-- 商家网格 -->
          <div v-else class="merchantlist-grid">
            <div
              v-for="merchant in paginatedMerchantList"
              :key="merchant.id"
              class="merchantlist-card"
              @click="viewMerchantDetail(merchant)"
            >
              <!-- 商家图片 -->
              <div class="merchantlist-image">
                <img
                  :src="getMerchantImage(merchant)"
                  :alt="merchant.merchant.name"
                />
                <div class="merchantlist-category">
                  {{ getServiceCategory(merchant.merchant.service) }}
                </div>
              </div>

              <!-- 商家信息 -->
              <div class="merchantlist-content">
                <h3 class="merchantlist-name">
                  {{ merchant.merchant.name }}
                </h3>
                <p class="merchantlist-remark">
                  {{ getShortRemark(merchant.merchant.remark) }}
                </p>
              </div>

              <div class="merchant-info">
                <div class="merchant-details">
                  <div class="detail-item">
                    <i class="el-icon-location"></i>
                    <span>{{ getAreaName(merchant.merchant.area) }}</span>
                  </div>
                  <div class="detail-item">
                    <i class="el-icon-time"></i>
                    <span>{{ getBusinessHours(merchant.merchant) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 分页 -->
        <div v-if="filteredMerchantList.length > 0" class="pagination-wrapper">
          <div class="pagination">
            <button
              class="page-btn"
              :disabled="currentPage === 1"
              @click="changePage(currentPage - 1)"
            >
              <i class="el-icon-arrow-left"></i>
              上一页
            </button>
            <div class="page-numbers">
              <button
                v-for="page in visiblePages"
                :key="page"
                class="page-number"
                :class="{ active: page === currentPage }"
                @click="changePage(page)"
              >
                {{ page }}
              </button>
            </div>
            <button
              class="page-btn"
              :disabled="currentPage === totalPages"
              @click="changePage(currentPage + 1)"
            >
              下一页
              <i class="el-icon-arrow-right"></i>
            </button>
          </div>
          <div class="pagination-info">
            共 {{ filteredMerchantList.length }} 条记录，第 {{ currentPage }} /
            {{ totalPages }} 页
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// 导入图片处理工具
import { getImageUrl } from "@/utils/imageUtils";
// 导入API（已登录商户分页接口）
import { merchantAPI, noPermissionRequired } from "@/api/index";

export default {
  name: "MerchantNotLoggedList",
  data() {
    return {
      loading: false,
      merchantList: [],
      currentPage: 1,
      pageSize: 50, // 已登录接口默认大小示例为10，可根据需要调整
      totalCount: 0,
      // 服务分类映射 - 将从API动态获取
      serviceCategories: {},
      // 搜索相关
      searchKeyword: "", // 搜索关键词
    };
  },
  computed: {
    // 过滤后的商家列表
    filteredMerchantList() {
      if (!this.searchKeyword.trim()) {
        return this.merchantList;
      }

      const keyword = this.searchKeyword.toLowerCase();
      return this.merchantList.filter((merchant) =>
        merchant.merchant?.name?.toLowerCase().includes(keyword)
      );
    },

    // 服务端分页：接口已经分页，这里直接返回过滤后的（当前页）数据
    paginatedMerchantList() {
      return this.filteredMerchantList;
    },

    totalPages() {
      // 如果接口返回 pages，可由 loadMerchants 设置一个 this.pages；否则用 totalCount / pageSize
      return this.pages || Math.ceil(this.totalCount / this.pageSize) || 1;
    },
    visiblePages() {
      const total = this.totalPages;
      const current = this.currentPage;

      if (total <= 1) return [1];

      const delta = 2;
      const range = [];
      const rangeWithDots = [];

      for (
        let i = Math.max(2, current - delta);
        i <= Math.min(total - 1, current + delta);
        i++
      ) {
        range.push(i);
      }

      if (current - delta > 2) {
        rangeWithDots.push(1, "...");
      } else {
        rangeWithDots.push(1);
      }

      rangeWithDots.push(...range);

      if (current + delta < total - 1) {
        rangeWithDots.push("...", total);
      } else if (total > 1) {
        rangeWithDots.push(total);
      }

      return rangeWithDots.filter(
        (item, index, arr) => arr.indexOf(item) === index
      );
    },
  },
  created() {
    this.initData();
  },
  watch: {
    // 监听搜索关键词变化，自动重置页码
    searchKeyword() {
      this.currentPage = 1;
    },
  },
  methods: {
    /**
     * 初始化数据
     */
    async initData() {
      this.loading = true;
      try {
        await Promise.all([this.loadServiceCategories(), this.loadMerchants()]);
      } catch (error) {
        console.error("初始化数据失败:", error);
        this.$msg.error("加载数据失败，请刷新页面重试");
      } finally {
        this.loading = false;
      }
    },

    /**
     * 加载服务分类
     */
    async loadServiceCategories() {
      try {
        const response = await noPermissionRequired.getServiceTypeMaster({
          current: 1,
          size: 999, // 获取所有分类
        });

        const categories = response?.data?.records || [];

        // 构建分类映射对象
        this.serviceCategories = {};
        categories.forEach((category) => {
          this.serviceCategories[category.id] = category.title;
        });

        console.log("服务分类加载成功:", this.serviceCategories);
      } catch (error) {
        console.error("加载服务分类失败:", error);
        // 使用默认分类作为fallback
        this.serviceCategories = {
          1: "餐饮服务",
          2: "健康养生",
          3: "技术咨询",
          4: "财税服务",
        };
      }
    },

    /**
     * 加载商家列表
     * @param {boolean} showLoading - 是否显示加载状态
     */
    async loadMerchants(showLoading = false) {
      if (showLoading) this.loading = true;
      try {
        // 已登录接口：/api/admin/merchant/page 需要 auditStatus=2（审核通过）
        const params = {
          current: this.currentPage,
          size: this.pageSize,
          auditStatus: 2,
          descs: "create_time",
        };
        // 搜索联动服务端
        if (this.searchKeyword && this.searchKeyword.trim()) {
          params.name = this.searchKeyword.trim();
        }
        const response = await merchantAPI.getMerchantPage(params);
        const data = response?.data || {};
        const records = Array.isArray(data.records) ? data.records : [];
        // 包装为 { merchant: 原始对象 } 以复用模板结构
        const wrapped = records.map((r) => ({ id: r.id, merchant: r }));
        // 过滤非法 & 被封禁 auditStatus === '-2'
        this.merchantList = wrapped.filter(
          (item) => item.merchant && item.merchant.auditStatus !== "-2"
        );
        this.totalCount = data.total || this.merchantList.length;
        this.pages = data.pages || Math.ceil(this.totalCount / this.pageSize);
      } catch (error) {
        console.error("加载已登录商户分页失败:", error);
        this.$msg.error("加载商户列表失败，请稍后重试");
        this.merchantList = [];
        this.totalCount = 0;
        this.pages = 1;
      } finally {
        if (showLoading) this.loading = false;
      }
    },

    /**
     * 获取商家图片
     */
    getMerchantImage(merchant) {
      if (merchant.merchant.merchantCover) {
        return getImageUrl(merchant.merchant.merchantCover);
      }
      return require("@/assets/logo.png");
    },

    /**
     * 获取服务分类名称
     */
    getServiceCategory(serviceId) {
      return this.serviceCategories[serviceId] || "其他服务";
    },

    /**
     * 获取营业时间文本
     */
    getBusinessHours(merchant) {
      return `${merchant.businessStartTime} - ${merchant.businessEndTime}`;
    },

    /**
     * 获取简短描述
     */
    getShortRemark(remark) {
      if (!remark) return "暂无介绍";
      return remark.length > 60 ? remark.substring(0, 60) + "..." : remark;
    },

    /**
     * 获取地区名称
     */
    getAreaName(area) {
      if (!area) return "未知地区";
      // 提取地区名称，去掉省市前缀
      return area.split("市")[1] || area.split("省")[1] || area;
    },

    /**
     * 查看商家详情
     */
    viewMerchantDetail(merchant) {
      if (!merchant?.merchant?.id) {
        this.$msg.warning("商家信息不完整");
        return;
      }

      // 检查商家是否被封禁
      if (merchant.merchant.auditStatus === "-2") {
        this.$msg.error("此商家已被封禁");
        return;
      }

      this.$router
        .push({
          name: "merchantDetail",
          params: { id: merchant.merchant.id },
        })
        .catch((err) => {
          console.error("跳转到商家详情失败:", err);
          this.$msg.error("跳转失败，请稍后重试");
        });
    },

    /**
     * 处理搜索
     */
    handleSearch() {
      // 重置页码并重新请求服务端
      this.currentPage = 1;
      this.loadMerchants(true);
    },

    /**
     * 清除搜索
     */
    clearSearch() {
      this.searchKeyword = "";
      this.currentPage = 1;
    },

    /**
     * 分页切换
     */
    changePage(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage)
        return;
      this.currentPage = page;
      this.loadMerchants(true);
    },
  },
};
</script>

<style lang="scss" scoped>
// 红色主题色彩定义
$primary-red: #e74c3c;
$primary-red-dark: #c0392b;

.merchantlist-page {
  min-height: 100vh;
  background: #f8f9fa;
  padding: 16px 0;

  // 防止外部样式污染
  * {
    box-sizing: border-box;
  }

  // 重置可能被污染的样式
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0;
    font-weight: normal;
  }

  p {
    margin: 0;
  }

  ul,
  ol {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  button {
    border: none;
    background: none;
    cursor: pointer;
    font-family: inherit;
  }

  img {
    max-width: 100%;
    height: auto;
    display: block;
  }
}

.merchantlist-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
}

.merchantlist-header {
  text-align: center;
  margin-bottom: 24px;
  padding: 20px 0;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  h1 {
    font-size: 24px;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 8px;
    letter-spacing: -0.2px;
  }

  p {
    font-size: 14px;
    color: #7f8c8d;
    font-weight: 400;
  }
}

/* 搜索筛选区域 */
.merchantlist-filter {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  .filter-section {
    .search-box {
      display: flex;
      align-items: center;
      gap: 16px;
      margin-bottom: 12px;

      .filter-label {
        font-size: 14px;
        font-weight: 500;
        color: #333;
        white-space: nowrap;
      }

      .search-input-wrapper {
        position: relative;
        flex: 1;
        max-width: 400px;

        .search-input {
          width: 100%;
          padding: 10px 40px 10px 12px;
          border: 1px solid #dcdfe6;
          border-radius: 6px;
          font-size: 14px;
          outline: none;
          transition: border-color 0.3s ease;

          &:focus {
            border-color: $primary-red;
          }

          &::placeholder {
            color: #c0c4cc;
          }
        }

        .clear-btn {
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #f5f7fa;
          color: #909399;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;

          &:hover {
            background: $primary-red;
            color: white;
          }

          i {
            font-size: 12px;
          }
        }
      }
    }

    .search-status {
      font-size: 13px;
      color: #666;
      padding: 8px 12px;
      background: #f8f9fa;
      border-radius: 6px;
      border-left: 3px solid $primary-red;

      span {
        font-weight: 500;
      }
    }
  }
}

/* 加载状态 */
.merchantlist-loading-wrapper {
  .merchantlist-skeleton-container {
    column-count: 3;
    column-gap: 20px;

    .merchantlist-skeleton-card {
      background: white;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      break-inside: avoid;
      margin-bottom: 20px;
      display: inline-block;
      width: 100%;

      .merchantlist-skeleton-image {
        width: 100%;
        height: 120px;
        background: #f0f0f0;

        &:nth-child(3n + 1) {
          height: 160px;
        }

        &:nth-child(3n + 2) {
          height: 140px;
        }

        &:nth-child(3n) {
          height: 180px;
        }
      }

      .skeleton-content {
        padding: 16px;

        .merchantlist-skeleton-line {
          background: #f0f0f0;
          border-radius: 4px;
          margin-bottom: 8px;

          &.name {
            width: 90%;
            height: 20px;
          }

          &.desc {
            width: 100%;
            height: 16px;

            &:last-of-type {
              width: 80%;
            }
          }

          &.location {
            width: 70%;
            height: 14px;
            margin-bottom: 0;
          }
        }
      }
    }
  }
}

/* 空状态 */
.merchantlist-empty-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 8px;
  color: #999;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);

  i {
    font-size: 48px;
    margin-bottom: 16px;
    display: block;
    color: $primary-red;
  }

  p {
    font-size: 14px;
  }
}

/* 商家瀑布流网格 */
.merchantlist-grid {
  column-count: 3;
  column-gap: 20px;
  margin-top: 8px;

  .merchantlist-card {
    background: white;
    border: 1px solid #e8e8e8;
    border-radius: 12px;
    overflow: hidden;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
    break-inside: avoid;
    margin-bottom: 20px;
    display: inline-block;
    width: 100%;

    &:hover {
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
      border-color: $primary-red;
      transform: translateY(-2px);
    }

    .merchantlist-image {
      width: 100%;
      height: auto;
      position: relative;
      overflow: hidden;

      img {
        width: 100%;
        height: auto;
        object-fit: contain;
        display: block;
        max-height: none;
      }

      .merchantlist-category {
        position: absolute;
        top: 12px;
        right: 12px;
        font-size: 12px;
        color: white;
        background: rgba(231, 76, 60, 0.9);
        padding: 4px 8px;
        border-radius: 12px;
        font-weight: 600;
        backdrop-filter: blur(4px);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
      }
    }

    .merchantlist-content {
      padding: 16px;

      .merchantlist-name {
        font-size: 18px;
        font-weight: 600;
        color: #333;
        line-height: 1.3;
        margin: 0 0 12px 0;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
      }

      .merchantlist-remark {
        font-size: 14px;
        color: #666;
        margin: 0;
        line-height: 1.5;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
    }

    .merchant-info {
      padding: 16px;
      border-top: 1px solid #f5f5f5;

      .merchant-details {
        display: flex;
        flex-direction: column;
        gap: 8px;

        .detail-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: #888;

          i {
            width: 14px;
            text-align: center;
            color: $primary-red;
            font-size: 14px;
            flex-shrink: 0;
          }

          span {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            flex: 1;
          }
        }
      }
    }
  }
}

/* 分页 */
.pagination-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 32px;
  padding: 24px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .pagination {
    display: flex;
    align-items: center;
    gap: 8px;

    .page-btn {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 8px 12px;
      background: white;
      border: 1px solid #dcdfe6;
      border-radius: 4px;
      color: #333;
      cursor: pointer;

      &:hover:not(:disabled) {
        border-color: $primary-red;
        color: $primary-red;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .page-numbers {
      display: flex;
      gap: 4px;

      .page-number {
        width: 32px;
        height: 32px;
        border: 1px solid #dcdfe6;
        border-radius: 4px;
        background: white;
        color: #333;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
          border-color: $primary-red;
          color: $primary-red;
        }

        &.active {
          background: $primary-red;
          border-color: $primary-red;
          color: white;
        }
      }
    }
  }

  .pagination-info {
    font-size: 14px;
    color: #7f8c8d;
  }
}

/* Element UI 组件样式保护 */
.merchantlist-page {
  // 按钮样式保护
  ::v-deep .el-button {
    font-family: inherit !important;
    border-radius: 4px !important;
  }

  // 选择框样式保护
  ::v-deep .el-select {
    .el-input__inner {
      border-radius: 4px !important;
      border-color: #dcdfe6 !important;

      &:focus {
        border-color: $primary-red !important;
      }
    }
  }

  // 图标样式保护
  ::v-deep .el-icon {
    font-family: element-icons !important;
  }
}

/* 响应式瀑布流 */
@media (max-width: 1200px) {
  .merchantlist-grid {
    column-count: 2;
    column-gap: 16px;
  }

  .merchantlist-skeleton-container {
    column-count: 2;
    column-gap: 16px;
  }
}

@media (max-width: 768px) {
  .merchantlist-grid {
    column-count: 1;
    column-gap: 0;

    .merchantlist-card {
      margin-bottom: 16px;
    }
  }

  .merchantlist-skeleton-container {
    column-count: 1;
    column-gap: 0;

    .merchantlist-skeleton-card {
      margin-bottom: 16px;
    }
  }

  .merchantlist-container {
    padding: 0 12px;
  }

  .merchantlist-filter {
    padding: 16px;

    .filter-section .search-box {
      flex-direction: column;
      align-items: stretch;
      gap: 12px;

      .filter-label {
        align-self: flex-start;
      }

      .search-input-wrapper {
        max-width: none;
      }
    }
  }
}
</style>
